import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, Send, ChevronDown, ShieldCheck, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/shared";

export const SendConsentDialog = ({ open, onOpenChange, patients, presetPatientId }) => {
  const [selectedPatientId, setSelectedPatientId] = useState(presetPatientId || '');
  const [sending, setSending] = useState(false);
  useEffect(() => { if (open) setSelectedPatientId(presetPatientId || ''); }, [open, presetPatientId]);
  const send = async () => {
    if (!selectedPatientId) { toast.error('Choose a patient'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/hipaa/send`, { patient_id: selectedPatientId });
      toast.success(`HIPAA notice & consent sent to ${r.data.to}`);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="send-consent-dialog">
        <DialogHeader>
          <DialogTitle>Email HIPAA Notice & Consent for Treatment</DialogTitle>
          <DialogDescription>The patient receives the full notice plus a secure link to review and sign electronically. The signature is saved to their chart automatically.</DialogDescription>
        </DialogHeader>
        {!presetPatientId && (
          <div className="space-y-2">
            <Label>Patient</Label>
            <Select value={selectedPatientId} onValueChange={setSelectedPatientId}>
              <SelectTrigger data-testid="send-consent-patient"><SelectValue placeholder="Choose patient..." /></SelectTrigger>
              <SelectContent>{patients.filter(p => p.email).map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email})</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending} data-testid="send-consent-submit" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold">{sending ? 'Sending...' : 'Send for e-signature'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const HipaaDisclosure = ({ patients }) => {
  const [doc, setDoc] = useState(null);
  const [sendModal, setSendModal] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hipaa/disclosure`).then(r => setDoc(r.data)).catch(() => toast.error('Failed to load HIPAA notice'));
  }, []);

  if (!doc) return <p className="text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-5 print-area" data-testid="hipaa-page">
      <div className="flex justify-between items-start gap-4 no-print flex-wrap">
        <div>
          <h2 className="text-2xl font-bold brand-heading gold-gradient-text">{doc.title}</h2>
          <p className="text-xs text-muted-foreground mt-1">Effective {doc.effective_date} · Click any topic below to read it. Staff can email the full notice + consent to a patient to sign in advance, or sign together during the first visit.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-hipaa-btn"><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button onClick={() => setSendModal(true)} data-testid="send-hipaa-btn" className="bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold"><Send className="w-4 h-4 mr-1" />Send to Patient</Button>
        </div>
      </div>

      <div className="text-center pb-4 border-b border-border/40 print-only-block">
        <h1 className="text-2xl font-bold gold-gradient-text brand-heading">EliteIV Lounge</h1>
        <p className="text-xs text-muted-foreground tracking-widest uppercase mt-1">Patient Privacy Notice & Consent</p>
        <h2 className="text-xl font-semibold mt-4">{doc.title}</h2>
        <p className="text-xs text-muted-foreground mt-2">Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className="space-y-3" data-testid="hipaa-sections">
        {doc.sections.map(([title, body], i) => {
          const open = openIdx === i;
          const Icon = /consent/i.test(title) ? PenLine : /rights/i.test(title) ? ShieldCheck : null;
          return (
            <div key={i} className={`rounded-xl border-2 transition-colors ${open ? 'border-primary bg-primary/10' : 'border-primary/50 bg-black/30 hover:border-primary'}`} data-testid={`hipaa-section-${i}`}>
              <button type="button" onClick={() => setOpenIdx(open ? null : i)} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left no-print" data-testid={`hipaa-section-toggle-${i}`}>
                <span className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg border-2 border-primary/70 text-primary font-bold text-sm bg-primary/10">{i + 1}</span>
                  <span className="text-lg md:text-xl font-extrabold tracking-wide text-primary uppercase">{title}</span>
                  {Icon && <Icon className="w-4 h-4 text-primary/70" />}
                </span>
                <ChevronDown className={`w-5 h-5 text-primary transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
              <div className={`px-5 pb-5 text-sm leading-relaxed text-foreground/90 ${open ? '' : 'hidden print:block'}`} data-testid={`hipaa-section-body-${i}`}>
                <p className="hidden print:block font-bold text-primary mb-1">{title}</p>
                {body}
              </div>
            </div>
          );
        })}
        <div className="border-2 border-primary/60 bg-primary/5 rounded-xl p-5 mt-6">
          <p className="text-lg font-extrabold text-primary uppercase tracking-wide mb-2">Patient Acknowledgment</p>
          <p className="text-muted-foreground text-sm">{doc.patient_acknowledgment}</p>
          <div className="grid grid-cols-2 gap-6 mt-6">
            <div className="border-b border-foreground/40 pb-1"><p className="text-xs text-muted-foreground">Patient Signature</p></div>
            <div className="border-b border-foreground/40 pb-1"><p className="text-xs text-muted-foreground">Date</p></div>
          </div>
        </div>
      </div>

      <SendConsentDialog open={sendModal} onOpenChange={setSendModal} patients={patients} />
    </div>
  );
};
